import axios from 'axios';
import { useEffect, useState } from 'react';
import TemplateCard from './components/TemplateCard';
import InfoCard from './components/InfoCard';
import InputCard from './components/InputCard';
import ResultPanel from './components/ResultPanel';
import GuideModal from './components/GuideModal';
import {
  INFO_FIELDS,
  EMPTY_INFO,
  minutesToText,
  remapInfo,
  sameFields,
  formatDuration,
  probeAudioDuration,
} from './minutes';
import {
  Wrapper,
  Header,
  Brand,
  Hero,
  Badge,
  Main,
  Column,
  Footer,
  SubmitArea,
  PrimaryButton,
  TextButton,
} from './App.styles';

const API_BASE_URL = '/api';

// 백엔드 MAX_TRANSCRIPT_CHARS 와 맞춘다
const MAX_CHARS = 100000;

function errorMessage(err, fallback) {
  return err.response?.data?.error || err.userMessage || fallback;
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const TRANSCRIBE_STAGE = {
  queued: () => '앞선 변환이 끝나기를 기다리고 있습니다...',
  decoding: () => '녹음 파일을 읽고 있습니다...',
  loading: (progress) => `음성 인식 모델을 준비하고 있습니다... ${progress}% (처음 한 번만)`,
  transcribing: (progress) => `음성을 글로 바꾸고 있습니다... ${progress}%`,
  waiting: (progress, wait) =>
    `무료 사용량 한도로 잠시 기다리는 중입니다... 약 ${formatDuration(wait)} (진행 ${progress}%)`,
};

// 음성 파일을 올리고 변환이 끝날 때까지 진행 상황을 조회한다
async function transcribeAudio(file, onMessage) {
  const formData = new FormData();
  formData.append('audio', file);

  onMessage('녹음 파일을 올리고 있습니다...');
  const { data } = await axios.post(`${API_BASE_URL}/transcribe`, formData, {
    onUploadProgress: (e) => {
      if (e.total) onMessage(`녹음 파일을 올리고 있습니다... ${Math.round((e.loaded / e.total) * 100)}%`);
    },
  });

  for (;;) {
    await wait(2000);
    const { data: job } = await axios.get(`${API_BASE_URL}/transcribe/${data.jobId}`);
    if (job.status === 'done') return job.text;
    if (job.status === 'error') {
      const error = new Error(job.error);
      error.userMessage = job.error;
      throw error;
    }
    onMessage((TRANSCRIBE_STAGE[job.stage] || TRANSCRIBE_STAGE.queued)(job.progress, job.wait));
  }
}

function App() {
  const [showGuide, setShowGuide] = useState(false);

  // 1. 템플릿
  const [templateMode, setTemplateMode] = useState('default'); // default | custom
  // { file, filename, mode: placeholder | auto, mapping, infoFields, recognized, missing }
  const [template, setTemplate] = useState(null);
  const [templateUploading, setTemplateUploading] = useState(false);

  // 2. 회의 정보
  const [info, setInfo] = useState(EMPTY_INFO);

  // 3. 회의 기록
  const [inputMode, setInputMode] = useState('text'); // text | file | audio
  const [transcript, setTranscript] = useState('');
  const [recordFile, setRecordFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  // 변환한 텍스트는 같은 파일로 다시 시도할 때 재사용한다 (음성 변환이 오래 걸리므로)
  const [audioTranscript, setAudioTranscript] = useState(null); // { file, text }
  const [audioDuration, setAudioDuration] = useState(null); // 브라우저에서 읽은 녹음 길이(초)
  // 음성 변환 사용량 { engine: groq | local, remainingSeconds, blockedUntil }
  const [quota, setQuota] = useState(null);

  // 결과
  const [status, setStatus] = useState('idle'); // idle | loading | error | done
  const [loadingMessage, setLoadingMessage] = useState(null);
  const [minutes, setMinutes] = useState(null);
  const [minutesFields, setMinutesFields] = useState(INFO_FIELDS); // 정리할 때 쓴 회의 정보 항목
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const usingTemplate = templateMode === 'custom' && template !== null;
  const templateName = usingTemplate ? template.filename : '기본 템플릿';
  // 회의 정보 입력란: 내 템플릿이면 양식에 있는 항목, 아니면 기본 항목
  const activeFields = usingTemplate ? template.infoFields : INFO_FIELDS;

  // 항목 목록이 바뀌면 입력해 둔 값은 같은 이름의 항목으로 옮긴다
  const switchFields = (nextFields) => {
    setInfo((prev) => remapInfo(prev, activeFields, nextFields));
  };

  // 무료 사용량을 다 썼거나 녹음이 남은 시간보다 길면 음성 변환을 막는다
  const limited = quota && quota.engine === 'groq';
  const quotaExhausted = limited && quota.remainingSeconds <= 0;
  const audioTooLong = limited && audioDuration !== null && audioDuration > quota.remainingSeconds;

  const canSubmit =
    status !== 'loading' &&
    ((inputMode === 'text' && transcript.trim() !== '' && transcript.length <= MAX_CHARS) ||
      (inputMode === 'file' && recordFile !== null) ||
      (inputMode === 'audio' && audioFile !== null && !quotaExhausted && !audioTooLong));

  const refreshQuota = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/transcribe/quota`);
      setQuota(data);
    } catch (err) {
      setQuota(null);
    }
  };

  // 음성 업로드 탭을 열 때마다 남은 사용량을 새로 확인한다
  useEffect(() => {
    if (inputMode === 'audio') refreshQuota();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputMode]);

  const handleAudioFileChange = async (file) => {
    setAudioFile(file);
    setAudioDuration(null);
    if (file) setAudioDuration(await probeAudioDuration(file));
  };

  const handleTemplateUpload = async (file) => {
    setTemplateUploading(true);
    try {
      const formData = new FormData();
      formData.append('template', file);
      const response = await axios.post(`${API_BASE_URL}/template/inspect`, formData);
      switchFields(response.data.infoFields);
      setTemplate({ file, ...response.data });
      setTemplateMode('custom');
    } catch (err) {
      alert(errorMessage(err, '템플릿을 읽을 수 없습니다.'));
    } finally {
      setTemplateUploading(false);
    }
  };

  const handleSelectDefault = () => {
    if (templateMode === 'default') return;
    switchFields(INFO_FIELDS);
    setTemplateMode('default');
  };

  const handleInfoChange = (key, value) => {
    setInfo((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setStatus('loading');
    setLoadingMessage(null);
    setError(null);
    setCopied(false);

    try {
      const fields = activeFields;
      const formData = new FormData();
      formData.append('info', JSON.stringify(info));
      formData.append('fields', JSON.stringify(fields.map(({ key, label }) => ({ key, label }))));
      if (inputMode === 'file') {
        formData.append('file', recordFile);
      } else if (inputMode === 'audio') {
        let text = audioTranscript && audioTranscript.file === audioFile ? audioTranscript.text : null;
        if (!text) {
          try {
            text = await transcribeAudio(audioFile, setLoadingMessage);
          } finally {
            refreshQuota();
          }
          setAudioTranscript({ file: audioFile, text });
        }
        formData.append('transcript', text);
      } else {
        formData.append('transcript', transcript);
      }

      setLoadingMessage(null);
      const response = await axios.post(`${API_BASE_URL}/minutes`, formData);
      setMinutes(response.data.minutes);
      setMinutesFields(response.data.fields);
      setStatus('done');
    } catch (err) {
      setError(errorMessage(err, '잠시 후 다시 시도해 주세요.'));
      setStatus('error');
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(minutesToText(minutes, minutesFields));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert('복사하지 못했습니다. 브라우저 권한을 확인해 주세요.');
    }
  };

  const handleDownload = async () => {
    // 정리한 뒤 템플릿을 바꾸면 항목이 맞지 않으므로 다시 정리하도록 안내한다
    if (!sameFields(activeFields, minutesFields)) {
      alert('템플릿이 바뀌어 회의 정보 항목이 달라졌습니다. "AI로 회의록 정리하기"를 다시 눌러 주세요.');
      return;
    }

    setDownloading(true);
    try {
      const formData = new FormData();
      formData.append('minutes', JSON.stringify(minutes));
      formData.append('fields', JSON.stringify(minutesFields));
      if (usingTemplate) {
        formData.append('template', template.file);
        if (template.mode === 'auto') {
          formData.append('mapping', JSON.stringify(template.mapping));
        }
      }

      const response = await axios.post(`${API_BASE_URL}/minutes/docx`, formData, { responseType: 'blob' });

      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename\*=UTF-8''([^;]+)/);
      const filename = match ? decodeURIComponent(match[1]) : '회의록.docx';

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Word 파일을 만들지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Wrapper>
      <Header>
        <Brand>
          <strong>BRICK</strong>
          <span>Meeting Minutes</span>
        </Brand>
        <TextButton onClick={() => setShowGuide(true)}>이용 가이드 보기</TextButton>
      </Header>

      <Hero>
        <h1>
          회의록 자동 정리 <Badge>Beta</Badge>
        </h1>
        <p>회의 내용을 넣으면 템플릿에 맞춰 정리해 드립니다</p>
      </Hero>

      <Main>
        <Column>
          <TemplateCard
            mode={templateMode}
            template={template}
            uploading={templateUploading}
            onSelectDefault={handleSelectDefault}
            onUpload={handleTemplateUpload}
          />
          <InfoCard fields={activeFields} info={info} fromTemplate={usingTemplate} onChange={handleInfoChange} />
          <InputCard
            mode={inputMode}
            onModeChange={setInputMode}
            transcript={transcript}
            onTranscriptChange={setTranscript}
            file={recordFile}
            onFileChange={setRecordFile}
            audioFile={audioFile}
            onAudioFileChange={handleAudioFileChange}
            audioDuration={audioDuration}
            quota={quota}
            quotaExhausted={quotaExhausted}
            audioTooLong={audioTooLong}
            maxChars={MAX_CHARS}
          />
          <SubmitArea>
            <PrimaryButton onClick={handleSubmit} disabled={!canSubmit}>
              {status === 'loading' ? '정리하는 중...' : 'AI로 회의록 정리하기'}
            </PrimaryButton>
            <small>입력한 내용은 저장되지 않습니다</small>
          </SubmitArea>
        </Column>

        <ResultPanel
          status={status}
          minutes={minutes}
          fields={minutesFields}
          previewFields={activeFields}
          error={error}
          templateName={templateName}
          isAudio={inputMode === 'audio'}
          loadingMessage={loadingMessage}
          copied={copied}
          downloading={downloading}
          onCopy={handleCopy}
          onDownload={handleDownload}
          onRetry={handleSubmit}
          onDismiss={() => setStatus(minutes ? 'done' : 'idle')}
        />
      </Main>

      <Footer>Copyright © 2026</Footer>

      {showGuide && <GuideModal onClose={() => setShowGuide(false)} />}
    </Wrapper>
  );
}

export default App;
